import { LanguageService } from './language.service';

/**
 * Selects between Swedish and English values based on current language
 *
 * @param swedishText - The Swedish translation
 * @param englishText - The English translation
 * @param currentLanguage - Optional language override
 * @returns The value matching the current language (defaults to Swedish)
 *
 * @example
 * ```typescript
 * const greeting = tFunction('Hej', 'Hello');
 * ```
 */
export function tFunction(swedishText: string, englishText: string, currentLanguage?: string | undefined): string {
  const lang = currentLanguage || LanguageService.getCurrentLanguage();
  return lang === LanguageService.English ? englishText : swedishText;
}

/**
 * Common text translations used across the application.
 * Contains frequently used UI strings in both Swedish and English.
 */
export const commonTexts = {
  and: tFunction('och', 'and'),
  arrivalTime: tFunction('Ankomsttid', 'Arrival time'),
  cancel: tFunction('Avbryt', 'Cancel'),
  close: tFunction('Stäng', 'Close'),
  departureTime: tFunction('Avgångstid', 'Departure time'),
  email: tFunction('E-post', 'Email'),
  from: tFunction('Från', 'From'),
  generalError: tFunction('Något gick fel, var god försök igen', 'An error occurred, please try again'),
  line: tFunction('Linje', 'Line'),
  no: tFunction('Nej', 'No'),
  platform: tFunction('Läge', 'Platform'),
  search: tFunction('Sök', 'Search'),
  stopArea: tFunction('Hållplats', 'Stop area'),
  to: tFunction('Till', 'To'),
  track: tFunction('Spår', 'Track'),
  yes: tFunction('Ja', 'Yes'),
};

/**
 * Translation helper function with common text shortcuts.
 *
 * Can be used as a function to translate custom text:
 * @example
 * ```typescript
 * const greeting = t('Hej', 'Hello');
 * ```
 *
 * Or access common translations directly:
 * @example
 * ```typescript
 * const emailLabel = t.email;
 * const closeLabel = t.close;
 * ```
 */
export const t = Object.assign(tFunction, commonTexts);
